//#region Imports

import { Component, EventEmitter, inject, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastService } from '../../../../../shared/components/toast/service/toast.service';
import type { ActivityContent, ActivityQuestion } from '../../../../../shared/models/interfaces/content.interface';
import { Subscription } from 'rxjs';
import { ModalStatusService } from '../../../../../shared/services/modal-status/modal-status.service';
import { SpinnerComponent } from '../../../../../shared/components/spinner/spinner.component';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { ButtonTypeEnum } from '../../../../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../../../../shared/components/button/types/button-radius.enum';
import type { ActivityProgress } from '../../../../../shared/models/interfaces/activity-progress.interface';
import { ACTIVITY_MINIMUM_SCORE, ACTIVITY_SCORE_SCALE, ActivityEvaluationService } from '../../../../../shared/services/activity-evaluation/activity-evaluation.service';
import { FinishedComponent } from './finished/finished.component';
import { getCrudErrors } from '../../../../../shared/utils/functions';
import type { ActivityEvaluationResult } from '../../../../../shared/models/interfaces/activity-evaluation.interface';

//#endregion

@Component({
  selector: 'l-content-activity',
  standalone: true,
  templateUrl: './content-activity.component.html',
  imports: [CommonModule, ReactiveFormsModule, SpinnerComponent, IconComponent, ButtonComponent, FinishedComponent],
})
export class ContentActivityComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly modalStatusService: ModalStatusService,
  ) {}

  //#endregion

  //#region Input

  @Input()
  public activity!: ActivityContent;

  @Input()
  public userId!: string;

  @Input()
  public classId: string = '';

  @Input()
  public isLastModule: boolean = false;

  @Output()
  public submitted = new EventEmitter<ActivityEvaluationResult>();

  @Output()
  public restart = new EventEmitter<boolean>();

  @Output()
  public nextModule = new EventEmitter<void>();

  @Output()
  public certificate = new EventEmitter<void>();

  @Output()
  public completeCourse = new EventEmitter<void>();

  @Input()
  public isCourseComplete: boolean = false;

  @Input()
  public isCompletingCourse: boolean = false;

  //#endregion

  //#region Public Properties

  public questionsForm!: FormGroup;

  public currentPage: number = 1;

  public questionsPerPage: number = 1;

  public totalPages: number = 1;

  public currentQuestions: ActivityQuestion[] = [];

  public isLoading: boolean = true;

  public isSubmitting: boolean = false;

  public isSubmitted: boolean = false;

  public userScore: number = 0;

  public totalScore: number = 0;

  public minimumScore: number = 0;

  public correctQuestionIds: string[] = [];

  public saveProgress: number = 0;

  public activityPassed: boolean | null = null;

  public resultMessage: string = '';

  public buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  //#endregion

  //#region Private Properties

  private readonly activityEvaluationService: ActivityEvaluationService = inject(ActivityEvaluationService);

  private readonly subscriptions: Subscription[] = [];

  //#endregion

  //#region Lifecycle Hooks

  public ngOnInit(): void {
    this.modalStatusService.open({
      title: 'Atenção',
      message: 'Você está iniciando a avaliação deste módulo. Caso não atinja a porcentagem mínima de acerto de 70%, deverá refazer todas as aulas deste módulo e repetir a avaliação.',
      actionLabel: 'Tudo bem, vamos começar!',
    });

    this.loadActivity();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  //#endregion

  //#region Public Methods

  public nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.saveCurrentPageAnswers();
      this.currentPage++;
      this.updateCurrentQuestions();
    }
  }

  public prevPage(): void {
    if (this.currentPage > 1) {
      this.saveCurrentPageAnswers();
      this.currentPage--;
      this.updateCurrentQuestions();
    }
  }

  public async submitAnswers(): Promise<void> {
    this.saveCurrentPageAnswers();

    if (!this.questionsForm.valid) {
      this.toastService.warn('Por favor, responda todas as questões.', 'Atenção');
      return;
    }

    this.isSubmitting = true;

    try {
      const progress = this.loadActivityProgress();
      progress.completedAt = new Date();

      const answersByQuestionId = new Map(progress.answers.map(answer => [answer.questionId, answer.selectedOptionIndex]));
      const evaluationResult = await this.activityEvaluationService.evaluate({
        userId: this.userId,
        contentId: this.activity.id,
        questions: this.activity.questions ?? [],
        answersByQuestionId,
      });

      this.activityEvaluationService.saveResult(evaluationResult);
      this.saveActivityProgress(progress);
      this.clearActivityProgress();

      this.applyEvaluationResult(evaluationResult);

      this.submitted.emit(evaluationResult);
      this.toastService.success('Respostas enviadas com sucesso!', 'Sucesso');
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }

  public canGoNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  public canGoPrev(): boolean {
    return this.currentPage > 1;
  }

  public selectOption(questionId: string, optionIndex: number): void {
    this.questionsForm.get(questionId)?.setValue(optionIndex);
  }

  public isOptionSelected(questionId: string, optionIndex: number): boolean {
    return this.questionsForm.get(questionId)?.value === optionIndex;
  }

  public isQuestionAnswered(questionId: string): boolean {
    const questionValue = this.questionsForm.get(questionId)?.value;

    return questionValue !== null && questionValue !== undefined;
  }

  public onForwardClick(): void {
    if (this.currentPage < this.totalPages) {
      this.nextPage();
      return;
    }

    this.submitAnswers();
  }

  public onRestart(): void {
    const shouldResetModuleProgress = this.isSubmitted && this.userScore < this.minimumScore;

    if (shouldResetModuleProgress) {
      this.activityEvaluationService.clearSavedResult(this.userId, this.activity.id);
    }

    this.isSubmitted = false;
    this.currentPage = 1;
    this.userScore = 0;
    this.activityPassed = null;
    this.resultMessage = '';
    this.correctQuestionIds = [];
    this.saveProgress = 0;
    this.clearActivityProgress();
    this.initializeForm();
    this.updateCurrentQuestions();
    this.restart.emit(shouldResetModuleProgress);
  }

  //#endregion

  //#region Private Methods

  private async loadActivity(): Promise<void> {
    try {
      if (!this.activity?.questions) {
        this.toastService.error('Atividade não encontrada.', 'Erro');
        this.isLoading = false;
        return;
      }

      this.totalPages = Math.ceil(this.activity.questions.length / this.questionsPerPage);
      this.totalScore = ACTIVITY_SCORE_SCALE;
      this.minimumScore = ACTIVITY_MINIMUM_SCORE;
      this.initializeForm();
      this.loadSavedProgress();
      this.loadSavedEvaluationResult();
      this.updateCurrentQuestions();
      this.isLoading = false;

      if (this.activityEvaluationService.loadLatest) {
        const latest = await this.activityEvaluationService.loadLatest(this.userId, this.activity.id);

        if (latest) {
          this.applyEvaluationResult(latest);
        }
      }
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
      this.isLoading = false;
    }
  }

  private initializeForm(): void {
    const formGroup: { [key: string]: any } = {};

    this.activity.questions?.forEach(question => {
      formGroup[question.id] = this.formBuilder.control(
        null,
        Validators.required
      );
    });

    this.questionsForm = this.formBuilder.group(formGroup);
  }

  private loadSavedProgress(): void {
    const progress = this.loadActivityProgress();

    if (progress) {
      this.currentPage = progress.currentPage;
      
      progress.answers.forEach(answer => {
        const control = this.questionsForm.get(answer.questionId);
        if (control) {
          control.setValue(answer.selectedOptionIndex);
        }
      });

      this.saveProgress = (progress.answers.length / (this.activity.questions?.length ?? 1)) * 100;
    }
  }

  private loadSavedEvaluationResult(): void {
    const savedResult = this.activityEvaluationService.getSavedResult(this.userId, this.activity.id);

    if (!savedResult) {
      return;
    }

    this.applyEvaluationResult(savedResult);
  }

  private updateCurrentQuestions(): void {
    if (!this.activity.questions) {
      this.currentQuestions = [];
      return;
    }

    const startIndex = (this.currentPage - 1) * this.questionsPerPage;
    const endIndex = startIndex + this.questionsPerPage;
    this.currentQuestions = this.activity.questions.slice(startIndex, endIndex);
  }

  private saveCurrentPageAnswers(): void {
    const progress = this.loadActivityProgress();

    this.currentQuestions.forEach(question => {
      const value = this.questionsForm.get(question.id)?.value;
      
      if (value !== null && value !== undefined) {
        const existingIndex = progress.answers.findIndex(a => a.questionId === question.id);
        
        if (existingIndex > -1) {
          progress.answers[existingIndex].selectedOptionIndex = value;
        } else {
          progress.answers.push({
            questionId: question.id,
            selectedOptionIndex: value,
          });
        }
      }
    });

    this.saveActivityProgress(progress);
    this.saveProgress = (progress.answers.length / (this.activity.questions?.length ?? 1)) * 100;
  }

  private loadActivityProgress(): ActivityProgress {
    const key = this.getStorageKey();
    const stored = localStorage.getItem(key);

    if (stored) {
      return JSON.parse(stored) as ActivityProgress;
    }

    return {
      contentId: this.activity.id,
      userId: this.userId,
      currentPage: 1,
      answers: [],
    };
  }

  private saveActivityProgress(progress: ActivityProgress): void {
    const key = this.getStorageKey();
    localStorage.setItem(key, JSON.stringify(progress));
  }

  private clearActivityProgress(): void {
    const key = this.getStorageKey();
    localStorage.removeItem(key);
  }

  private getStorageKey(): string {
    return `activity-progress-${this.activity.id}-${this.userId}`;
  }

  private applyEvaluationResult(result: ActivityEvaluationResult): void {
    this.userScore = result.userScore;
    this.totalScore = ACTIVITY_SCORE_SCALE;
    this.minimumScore = result.minimumScore;
    this.activityPassed = result.approved;
    this.resultMessage = result.message ?? '';
    this.correctQuestionIds = result.correctQuestions.map(item => item.questionId);
    this.isSubmitted = true;
  }

  //#endregion

}
